import type { LinhaMapeada } from './validacao'
import { converterNumeroBR } from '../lib/textoBR'
import { conferirParidade, type AvisoParidade } from './paridadePreco'

export interface ProdutoComPreco {
  produto_id: string
  codigo: string
  descricao: string
}
export interface ItemPlanilha {
  codigo: string
  descricao: string
}
export interface Divergencias {
  novos: ItemPlanilha[]
  sumidos: ProdutoComPreco[]
  semPreco: ItemPlanilha[]
  // As duas guardas da caixa fechada. Vazio = nada a dizer, que e o caso de
  // toda planilha que nao usa embalagem.
  paridade: AvisoParidade[]
}

// "Sem preco" e a UNICA das tres listas que nao depende de historico: a resposta
// esta na propria linha da planilha. Mora aqui, sozinha, porque as duas funcoes
// abaixo precisam dela e duas copias divergiriam -- foi assim que as duas
// camadas passaram a discordar do sentido do zero na importacao.
//
// ⚠ PARECE, MAS NAO E, a gemea de `converterPrecoUnidade` (validacao.ts), e a
// diferenca foi MEDIDA em 29/08 antes de eu decidir nao juntar as duas:
//   - as duas leem `0` como "nao tem preco". Ate aqui sao iguais.
//   - o NEGATIVO nao: `-9,90` no preco AVULSO e erro de linha (R12); no preco da
//     TABELA ele passa hoje como preco valido e nao cai neste balde. Juntar as
//     funcoes mudaria isso em silencio, numa coluna que a R12 nao decidiu.
// Ou seja: a leitura do zero e a mesma, mas a do negativo nao. Enquanto for
// assim, sao duas funcoes -- e este comentario e o que impede a terceira copia.
function semPrecoNaLinha(l: LinhaMapeada): boolean {
  const precoTexto = l.campos.preco ?? ''
  const preco = precoTexto === '' ? null : converterNumeroBR(precoTexto)
  return preco === null || preco === 0
}

function itemDaLinha(l: LinhaMapeada): ItemPlanilha {
  return { codigo: l.campos.codigo, descricao: l.campos.descricao ?? '' }
}

// Compara a planilha (linhas válidas) com os produtos que já têm preço vivo
// naquela tabela. "novo" = na planilha com preço, sem preço na tabela;
// "sumido" = com preço na tabela, ausente da planilha; "sem preço" = na
// planilha com preço 0 ou em branco.
export function calcularDivergencias(
  linhas: LinhaMapeada[],
  produtosComPrecoNaTabela: ProdutoComPreco[],
): Divergencias {
  const codigosNaTabela = new Set(produtosComPrecoNaTabela.map((p) => p.codigo))
  const codigosNaPlanilha = new Set(linhas.map((l) => l.campos.codigo))
  const novos: ItemPlanilha[] = []
  const semPreco: ItemPlanilha[] = []
  for (const l of linhas) {
    const item = itemDaLinha(l)
    if (semPrecoNaLinha(l)) semPreco.push(item)
    else if (!codigosNaTabela.has(l.campos.codigo)) novos.push(item)
  }
  const sumidos = produtosComPrecoNaTabela.filter((p) => !codigosNaPlanilha.has(p.codigo))
  const paridade = conferirParidade(linhas)
  return { novos, sumidos, semPreco, paridade }
}

// Sem tabela anterior nao ha o que comparar, mas a planilha ainda fala sozinha:
// a conferencia de paridade nao depende de historico. Sem isto, a PRIMEIRA
// importacao de uma representada -- justamente a que tem duas colunas de preco,
// porque e o catalogo novo que traz a caixa fechada -- era a UNICA que nao tinha
// guarda nenhuma.
//
// ⚠ As duas perguntas sao separadas de proposito, e a trava do lado de la fica
// onde esta: *comparar com o que ja existia* so faz sentido com tabela anterior
// (sem ela, `calcularDivergencias` marcaria os 500 produtos como `novos` e a
// primeira importacao abriria uma tela listando 500 nomes); *conferir se os
// precos e o fator batem entre si* nao depende de historico nenhum.
//
// ⭐ `semPreco` entrou aqui em 29/08, por escolha dela: ele tambem nao depende de
// historico, e antes o produto sem preco entrava CALADO na primeira importacao e
// so aparecia na hora de vender. Da segunda importacao em diante a tela sempre
// mostrou e deixou digitar o preco ali -- agora as duas se comportam igual.
export function divergenciasSemHistorico(linhas: readonly LinhaMapeada[]): Divergencias {
  return {
    novos: [],
    sumidos: [],
    semPreco: linhas.filter(semPrecoNaLinha).map(itemDaLinha),
    paridade: conferirParidade(linhas),
  }
}

// A condicao que decide se a etapa de divergencias aparece. Mora aqui, e nao
// solta dentro da tela, porque ja aconteceu de um motivo novo ser calculado e a
// etapa continuar fechada -- o aviso existia e ninguem via. As duas guardas da
// caixa fechada ficaram assim de 28 a 29/08/2026: calculadas, testadas e
// invisiveis, porque o `if` da tela so citava os tres motivos antigos.
export function precisaAbrirDivergencias(d: Divergencias): boolean {
  return d.novos.length > 0 || d.sumidos.length > 0
    || d.semPreco.length > 0 || d.paridade.length > 0
}
